import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ShieldCheck, ArrowRight, X } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

export function RecoveryEmailNudge() {
  const { user } = useAuth();
  const hideKey = `so_hide_recovery_nudge_${user?.user_id || ""}`;
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(hideKey) === "1");

  const { data, isLoading } = useQuery({
    queryKey: ["account-recovery-status"],
    queryFn: async () => (await api.get("/account")).data,
  });

  if (isLoading || dismissed || !data) return null;
  if (data.recovery_email_verified) return null;

  const hasUnverified = !!data.recovery_email;
  const dismiss = () => { localStorage.setItem(hideKey, "1"); setDismissed(true); };

  return (
    <Card className="relative mt-6 p-4 pr-10 border-primary/30 bg-primary/5" data-testid="dashboard-recovery-nudge">
      {/* Stacks on phones — side-by-side columns forced the copy into a one-word-per-line
          sliver and stretched the page (reported 2026-08-18). */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <ShieldCheck className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-[600] text-foreground">
            {hasUnverified ? "Verify your recovery email" : "Add a recovery email"}
          </div>
          <div className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
            {hasUnverified
              ? "Check your inbox to confirm it — a verified backup helps you regain access if you're ever locked out."
              : "A private backup address helps you regain access if you ever lose your login email."}
          </div>
        </div>
        <Button asChild size="sm" className="w-full sm:w-auto sm:shrink-0" data-testid="dashboard-add-recovery-email">
          <Link to="/dashboard/account">
            {hasUnverified ? "Finish setup" : "Add recovery email"} <ArrowRight className="ml-1 h-4 w-4" />
          </Link>
        </Button>
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        title="Dismiss"
        data-testid="dashboard-dismiss-recovery-nudge"
        className="absolute right-3 top-3 text-muted-foreground transition-colors hover:text-foreground"
      >
        <X className="h-4 w-4" />
      </button>
    </Card>
  );
}
